import React, { useEffect, useRef } from 'react';

const MediaConverterAnimation = () => {
    const containerRef = useRef(null);
    const bgElementsRef = useRef(null);
    const progressRingCircleRef = useRef(null);
    const progressCounterRef = useRef(null);
    
    // Global variables like in the original HTML
    let mediaFiles = [];
    let processingProgress = 0;
    let isProcessing = false;
    let processingInterval = null;
    let nextYPosition = 150;
    const yStep = 100;
    let lastFileTime = 0;
    let colorIndex = 0;
    let mediaTypeIndex = 0;

    // Color schemes for alternating media icons
    const colorSchemes = [
        { buttonColor: "#ef4444", waveColor: "#ef4444" }, // Red
        { buttonColor: "#0695FF", waveColor: "#0695FF" }, // Blue
        { buttonColor: "#10b981", waveColor: "#10b981" }, // Green
        { buttonColor: "#8b5cf6", waveColor: "#8b5cf6" }, // Purple
        { buttonColor: "#eab308", waveColor: "#eab308" }  // Yellow
    ];

    // Output file types
    const outputFileTypes = [
        { type: 'MP3', color: '#0695FF' }, // Blue
        { type: 'MP4', color: '#f97316' }  // Orange
    ];

    // Media file types for input
    const mediaTypes = [
        {
            type: 'audio',
            createIcon: (colorScheme) => createAudioIcon(colorScheme),
            outputType: 'MP3'
        },
        {
            type: 'video',
            createIcon: (colorScheme) => createVideoIcon(colorScheme),
            outputType: 'MP4'
        }
    ];

    const createAudioIcon = (colorScheme) => {
        return `<div class="audio-icon">
            <div class="play-button" style="background-color: ${colorScheme.buttonColor};">
                <div class="play-triangle"></div>
            </div>
            <div class="waveform">
                <div class="wave-bar" style="background-color: ${colorScheme.waveColor}; animation: wave1 1.5s infinite;"></div>
                <div class="wave-bar" style="background-color: ${colorScheme.waveColor}; animation: wave2 1.5s infinite 0.1s;"></div>
                <div class="wave-bar" style="background-color: ${colorScheme.waveColor}; animation: wave3 1.5s infinite 0.2s;"></div>
                <div class="wave-bar" style="background-color: ${colorScheme.waveColor}; animation: wave4 1.5s infinite 0.3s;"></div>
                <div class="wave-bar" style="background-color: ${colorScheme.waveColor}; animation: wave5 1.5s infinite 0.4s;"></div>
                <div class="wave-bar" style="background-color: ${colorScheme.waveColor}; animation: wave6 1.5s infinite 0.5s;"></div>
                <div class="wave-bar" style="background-color: ${colorScheme.waveColor}; animation: wave7 1.5s infinite 0.6s;"></div>
                <div class="wave-bar" style="background-color: ${colorScheme.waveColor}; animation: wave8 1.5s infinite 0.7s;"></div>
            </div>
        </div>`;
    };

    const createVideoIcon = (colorScheme) => {
        return `<div class="video-icon">
            <div class="video-screen" style="background-color: ${colorScheme.buttonColor};">
                <div class="video-play"></div>
                <div class="video-timeline">
                    <div class="video-progress"></div>
                </div>
            </div>
            <div class="waveform video-waveform">
                <div class="wave-bar" style="background-color: ${colorScheme.waveColor}; animation: wave1 1.5s infinite;"></div>
                <div class="wave-bar" style="background-color: ${colorScheme.waveColor}; animation: wave2 1.5s infinite 0.1s;"></div>
                <div class="wave-bar" style="background-color: ${colorScheme.waveColor}; animation: wave3 1.5s infinite 0.2s;"></div>
                <div class="wave-bar" style="background-color: ${colorScheme.waveColor}; animation: wave4 1.5s infinite 0.3s;"></div>
                <div class="wave-bar" style="background-color: ${colorScheme.waveColor}; animation: wave5 1.5s infinite 0.4s;"></div>
                <div class="wave-bar" style="background-color: ${colorScheme.waveColor}; animation: wave6 1.5s infinite 0.5s;"></div>
            </div>
        </div>`;
    };

    const createDocumentIcon = (fileType, color) => {
        return `<div class="doc-file">
            <div class="doc-base" style="background-color: ${color};"></div>
            <div class="doc-corner"></div>
            <div class="doc-text">${fileType}</div>
        </div>`;
    };

    const setProgress = (percent) => {
        if (progressRingCircleRef.current && progressCounterRef.current) {
            const radius = 52;
            const circumference = radius * 2 * Math.PI;
            const offset = circumference - percent / 100 * circumference;
            progressRingCircleRef.current.style.strokeDashoffset = offset;
            progressCounterRef.current.textContent = `${Math.round(percent)}%`;
        }
    };

    const createMatrixCode = () => {
        const matrixChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+-=[]{}|;:,./<>?";
        const purpleColors = ['#a855f7', '#9333ea', '#c084fc', '#8b5cf6'];

        const createMatrixColumn = (xPercent, color) => {
            const column = document.createElement('div');
            column.className = 'matrix-column';
            column.style.left = `${xPercent}%`;
            
            const duration = Math.random() * 15 + 10;
            column.style.animation = `matrixRain ${duration}s linear infinite`;
            column.style.animationDelay = `${Math.random() * -20}s`;
            column.style.opacity = `${Math.random() * 0.4 + 0.6}`;
            column.style.color = color;
            column.style.textShadow = `0 0 5px ${color}`;

            const numChars = Math.floor(Math.random() * 15) + 10;
            
            for (let j = 0; j < numChars; j++) {
                const char = document.createElement('div');
                char.className = 'matrix-char';
                char.textContent = matrixChars.charAt(Math.floor(Math.random() * matrixChars.length));
                char.style.animationDelay = `${Math.random() * 2}s`;
                column.appendChild(char);

                setInterval(() => {
                    if (Math.random() > 0.8) {
                        char.textContent = matrixChars.charAt(Math.floor(Math.random() * matrixChars.length));
                    }
                }, 300);
            }

            if (bgElementsRef.current) {
                bgElementsRef.current.appendChild(column);
            }
        };

        // Create streams on the left side (0-30% of screen width)
        for (let i = 0; i < 8; i++) {
            createMatrixColumn(
                Math.random() * 30,
                purpleColors[Math.floor(Math.random() * purpleColors.length)]
            );
        }

        // Create streams on the right side (70-100% of screen width)
        for (let i = 0; i < 8; i++) {
            createMatrixColumn(
                70 + Math.random() * 30,
                purpleColors[Math.floor(Math.random() * purpleColors.length)]
            );
        }
    };

    const createMediaFile = () => {
        const now = Date.now();
        if (now - lastFileTime < 7000) {
            setTimeout(createMediaFile, 7000 - (now - lastFileTime));
            return;
        }

        if (mediaFiles.length >= 5) {
            setTimeout(createMediaFile, 2000);
            return;
        }

        lastFileTime = now;
        
        const currentColorScheme = colorSchemes[colorIndex % colorSchemes.length];
        colorIndex++;
        
        const currentMediaType = mediaTypes[mediaTypeIndex % mediaTypes.length];
        mediaTypeIndex++;
        
        const mediaFile = document.createElement('div');
        mediaFile.className = 'media-file';
        mediaFile.innerHTML = currentMediaType.createIcon(currentColorScheme);
        
        mediaFile.style.top = `${nextYPosition}px`;
        nextYPosition = (nextYPosition + yStep) % 400;
        if (nextYPosition < 150) nextYPosition = 150;
        
        const outputFileType = outputFileTypes.find(file => file.type === currentMediaType.outputType);
        
        const mediaInfo = {
            element: mediaFile,
            yPos: mediaFile.style.top,
            outputType: outputFileType.type,
            outputColor: outputFileType.color
        };
        
        mediaFiles.push(mediaInfo);
        if (containerRef.current) {
            containerRef.current.appendChild(mediaFile);
        }
        
        setTimeout(() => {
            if (!isProcessing) {
                isProcessing = true;
                processFiles();
            }
        }, 4000);
        
        setTimeout(createMediaFile, 7000);
    };

    const processFiles = () => {
        if (processingInterval) clearInterval(processingInterval);
        
        processingProgress = 0;
        setProgress(0);
        
        processingInterval = setInterval(() => {
            processingProgress += 1;
            setProgress(processingProgress);
            
            if (processingProgress >= 100) {
                clearInterval(processingInterval);
                
                if (mediaFiles.length > 0) {
                    const mediaInfo = mediaFiles.shift();
                    createConvertedFile(mediaInfo);
                }
                
                if (mediaFiles.length > 0) {
                    setTimeout(() => {
                        isProcessing = true;
                        processFiles();
                    }, 500);
                } else {
                    isProcessing = false;
                }
            }
        }, 30);
    };

    const createConvertedFile = (mediaInfo) => {
        const convertedFile = document.createElement('div');
        convertedFile.className = 'converted-file';
        
        convertedFile.innerHTML = `
            <div style="position: relative;">
                ${createDocumentIcon(mediaInfo.outputType, mediaInfo.outputColor)}
                <div class="file-progress">
                    <div class="file-progress-bar"></div>
                </div>
                <div class="checkmark">✓</div>
            </div>
        `;
        
        convertedFile.style.top = mediaInfo.yPos;
        convertedFile.style.left = `calc(50% + 100px)`;
        
        if (containerRef.current) {
            containerRef.current.appendChild(convertedFile);
        }
        
        setTimeout(() => {
            const progressBar = convertedFile.querySelector('.file-progress-bar');
            if (progressBar) {
                progressBar.style.width = '100%';
                
                setTimeout(() => {
                    if (progressBar) {
                        progressBar.style.width = '0';
                        const checkmark = convertedFile.querySelector('.checkmark');
                        if (checkmark) {
                            checkmark.style.opacity = '1';
                        }
                        
                        setTimeout(() => {
                            convertedFile.style.opacity = '0';
                            setTimeout(() => {
                                if (containerRef.current && convertedFile.parentNode) {
                                    containerRef.current.removeChild(convertedFile);
                                }
                            }, 500);
                        }, 500);
                    }
                }, 1500);
            }
        }, 500);
    };

    useEffect(() => {
        if (progressRingCircleRef.current) {
            const radius = 52;
            const circumference = radius * 2 * Math.PI;
            progressRingCircleRef.current.style.strokeDasharray = `${circumference} ${circumference}`;
            progressRingCircleRef.current.style.strokeDashoffset = circumference;
        }

        setProgress(0);
        createMatrixCode();
        createMediaFile();
    }, [createMediaFile]);

    return (
        <div className="media-converter-container bg-matrix">
            <style>{`
                .media-converter-container {
                    width: 100%;
                    height: 500px;
                    position: relative;
                    overflow: hidden;
                    background-color: #0c0020;
                    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                    transition: background 1s ease;
                }

                .media-file {
                    position: absolute;
                    left: -100px;
                    animation: moveToSphere 4s linear forwards;
                }

                .converted-file {
                    position: absolute;
                    opacity: 0;
                    transform: scale(0.5);
                    animation: fileAppear 3s forwards;
                }

                .processing-sphere {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    width: 180px;
                    height: 180px;
                    border-radius: 50%;
                    background: radial-gradient(circle at 30% 30%, #9333ea, #4c1d95);
                    box-shadow: 0 0 50px #9333ea;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 10;
                }

                .sphere-pulse {
                    position: absolute;
                    width: 100%;
                    height: 100%;
                    border-radius: 50%;
                    background: transparent;
                    border: 2px solid rgba(147, 51, 234, 0.5);
                    animation: pulse 2s infinite;
                }

                .progress-ring {
                    transform: rotate(-90deg);
                    position: absolute;
                }

                .progress-counter {
                    position: absolute;
                    color: white;
                    font-size: 24px;
                    font-weight: bold;
                    text-align: center;
                    z-index: 20;
                }

                .vertical-stream {
                    position: absolute;
                    left: 50%;
                    width: 40px;
                    transform: translateX(-50%);
                    background: linear-gradient(to bottom, rgba(147, 51, 234, 0) 0%, rgba(147, 51, 234, 0.3) 50%, rgba(147, 51, 234, 0) 100%);
                    z-index: 5;
                }

                .top-stream {
                    top: 0;
                    height: calc(50% - 90px);
                    animation: streamPulse 3s infinite;
                }

                .bottom-stream {
                    bottom: 0;
                    height: calc(50% - 90px);
                    animation: streamPulse 3s infinite 1.5s;
                }

                @keyframes streamPulse {
                    0%, 100% {
                        opacity: 0.3;
                    }
                    50% {
                        opacity: 0.6;
                    }
                }

                @keyframes pulse {
                    0% {
                        transform: scale(1);
                        opacity: 0.8;
                    }
                    100% {
                        transform: scale(1.5);
                        opacity: 0;
                    }
                }

                @keyframes moveToSphere {
                    0% {
                        left: -100px;
                        opacity: 0;
                        transform: translateY(0) scale(0.8);
                    }
                    20% {
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                    90% {
                        left: calc(50% - 40px);
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                    100% {
                        left: calc(50% - 20px);
                        opacity: 0;
                        transform: translateY(0) scale(0.5);
                    }
                }

                @keyframes fileAppear {
                    0% {
                        opacity: 0;
                        transform: scale(0.5) translateX(0);
                    }
                    20% {
                        opacity: 1;
                        transform: scale(1) translateX(0);
                    }
                    80% {
                        opacity: 1;
                        transform: scale(1) translateX(150px);
                    }
                    95% {
                        opacity: 0.5;
                        transform: scale(1.2) translateX(180px);
                    }
                    100% {
                        opacity: 0;
                        transform: scale(0.8) translateX(200px);
                    }
                }

                .file-progress {
                    position: absolute;
                    bottom: -15px;
                    left: 0;
                    width: 100%;
                    height: 6px;
                    background-color: rgba(255, 255, 255, 0.2);
                    border-radius: 3px;
                    overflow: hidden;
                }

                .file-progress-bar {
                    height: 100%;
                    width: 0%;
                    background-color: #10b981;
                    border-radius: 3px;
                    transition: width 1.5s ease-in-out;
                }

                .checkmark {
                    position: absolute;
                    bottom: -15px;
                    left: 50%;
                    transform: translateX(-50%);
                    color: #10b981;
                    font-size: 20px;
                    opacity: 0;
                }

                .glow {
                    position: absolute;
                    width: 100%;
                    height: 100%;
                    border-radius: 50%;
                    background: radial-gradient(circle, rgba(147, 51, 234, 0.8) 0%, rgba(147, 51, 234, 0) 70%);
                    filter: blur(15px);
                    z-index: -1;
                }

                .audio-icon {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    width: 60px;
                    height: 70px;
                }

                .play-button {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-bottom: 8px;
                    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
                }

                .play-triangle {
                    width: 0;
                    height: 0;
                    border-top: 10px solid transparent;
                    border-bottom: 10px solid transparent;
                    border-left: 15px solid white;
                    margin-left: 3px;
                }

                .waveform {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 50px;
                    height: 15px;
                }

                .video-waveform {
                    width: 40px;
                }

                .wave-bar {
                    width: 3px;
                    margin: 0 1px;
                    border-radius: 1px;
                }

                @keyframes wave1 {
                    0%, 100% { height: 5px; }
                    25% { height: 12px; }
                    50% { height: 7px; }
                    75% { height: 10px; }
                }

                @keyframes wave2 {
                    0%, 100% { height: 9px; }
                    25% { height: 5px; }
                    50% { height: 13px; }
                    75% { height: 7px; }
                }

                @keyframes wave3 {
                    0%, 100% { height: 13px; }
                    25% { height: 7px; }
                    50% { height: 10px; }
                    75% { height: 12px; }
                }

                @keyframes wave4 {
                    0%, 100% { height: 10px; }
                    25% { height: 13px; }
                    50% { height: 5px; }
                    75% { height: 9px; }
                }

                @keyframes wave5 {
                    0%, 100% { height: 7px; }
                    25% { height: 10px; }
                    50% { height: 12px; }
                    75% { height: 5px; }
                }

                @keyframes wave6 {
                    0%, 100% { height: 12px; }
                    25% { height: 9px; }
                    50% { height: 7px; }
                    75% { height: 13px; }
                }

                @keyframes wave7 {
                    0%, 100% { height: 4px; }
                    25% { height: 7px; }
                    50% { height: 9px; }
                    75% { height: 6px; }
                }

                @keyframes wave8 {
                    0%, 100% { height: 9px; }
                    25% { height: 4px; }
                    50% { height: 11px; }
                    75% { height: 7px; }
                }

                .video-icon {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    width: 60px;
                    height: 70px;
                }

                .video-screen {
                    width: 40px;
                    height: 30px;
                    border-radius: 4px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-bottom: 8px;
                    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
                    position: relative;
                    overflow: hidden;
                }

                .video-play {
                    width: 0;
                    height: 0;
                    border-top: 8px solid transparent;
                    border-bottom: 8px solid transparent;
                    border-left: 12px solid white;
                    margin-left: 2px;
                }

                .video-timeline {
                    position: absolute;
                    bottom: 4px;
                    left: 4px;
                    right: 4px;
                    height: 3px;
                    background-color: rgba(255, 255, 255, 0.3);
                    border-radius: 1.5px;
                }

                .video-progress {
                    height: 100%;
                    width: 60%;
                    background-color: white;
                    border-radius: 1.5px;
                }

                .bg-matrix {
                    background-color: #0c0020;
                    position: relative;
                    overflow: hidden;
                }

                .matrix-column {
                    position: absolute;
                    top: -20px;
                    font-family: monospace;
                    font-size: 14px;
                    color: #a855f7;
                    text-shadow: 0 0 5px #9333ea;
                    z-index: 0;
                    white-space: nowrap;
                    opacity: 0.8;
                }

                .matrix-char {
                    display: block;
                    text-align: center;
                    animation: fadeMatrix 2s infinite;
                }

                @keyframes fadeMatrix {
                    0%, 100% { opacity: 0.3; }
                    50% { opacity: 1; }
                }

                @keyframes matrixRain {
                    0% {
                        transform: translateY(-100%);
                    }
                    100% {
                        transform: translateY(400px);
                    }
                }

                .doc-file {
                    position: relative;
                    width: 50px;
                    height: 60px;
                }

                .doc-base {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    border-radius: 4px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
                }

                .doc-corner {
                    position: absolute;
                    top: 0;
                    right: 0;
                    width: 0;
                    height: 0;
                    border-style: solid;
                    border-width: 0 15px 15px 0;
                    border-color: transparent rgba(255, 255, 255, 0.3) transparent transparent;
                }

                .doc-text {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    color: white;
                    font-size: 14px;
                    font-weight: bold;
                    text-align: center;
                }

                .full-screen-container {
                    width: 100%;
                    height: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    position: relative;
                }

                .animation-container {
                    width: 100%;
                    height: 100%;
                    position: absolute;
                    top: 0;
                    left: 0;
                }
            `}</style>
            
            <div ref={bgElementsRef} id="bg-elements"></div>
            
            <div className="full-screen-container">
                <div className="vertical-stream top-stream"></div>
                <div className="vertical-stream bottom-stream"></div>
                
                <div className="processing-sphere">
                    <div className="sphere-pulse"></div>
                    <div className="sphere-pulse" style={{ animationDelay: '0.5s' }}></div>
                    <div className="glow"></div>
                    <svg className="progress-ring" width="120" height="120">
                        <circle 
                            className="progress-ring-circle" 
                            stroke="#ffffff" 
                            strokeOpacity="0.3"
                            strokeWidth="8" 
                            fill="transparent" 
                            r="52" 
                            cx="60" 
                            cy="60"/>
                        <circle 
                            ref={progressRingCircleRef}
                            className="progress-ring-circle-progress" 
                            stroke="#ffffff" 
                            strokeWidth="8" 
                            fill="transparent" 
                            r="52" 
                            cx="60" 
                            cy="60"
                            strokeDasharray="326.73"
                            strokeDashoffset="326.73"/>
                    </svg>
                    <div ref={progressCounterRef} className="progress-counter">0%</div>
                </div>

                <div ref={containerRef} className="animation-container"></div>
            </div>
        </div>
    );
};

export default MediaConverterAnimation; 